/**
 * Issues the backend raised about this coverage result.
 *
 * `explanation` is **not rendered**. It is English, written for logs, and real
 * UI validation found sentences like "Everything this project has captured of
 * the left side..." sitting in the middle of a Turkish screen. The text here is
 * composed in `i18n/issues.ts` from the structured fields - code, region,
 * measured_value, threshold - so the wording is ours and the numbers are the
 * backend's.
 *
 * `code` is a machine identifier and is not shown either. The region, when
 * present, is translated like everywhere else.
 */

import type { ProjectCoverageIssue } from "@/api/types";
import { StatusBadge } from "@/components/ui";
import { COVERAGE_REGION_LABELS, SEVERITY_LABELS, SEVERITY_TONES } from "@/i18n/terms";
import { presentIssue } from "@/i18n/issues";
import "./coverage.css";

const ORDER = { critical: 0, warning: 1, info: 2 } as const;

export function CoverageIssues({ issues }: { issues: ProjectCoverageIssue[] }) {
  if (issues.length === 0) return null;

  const sorted = [...issues].sort((a, b) => ORDER[a.severity] - ORDER[b.severity]);

  return (
    <section className="stack">
      <h3>Bulgular</h3>
      <ul className="issuelist">
        {sorted.map((issue, index) => {
          const text = presentIssue(issue);
          return (
            <li className="card issue" key={`${issue.code}-${issue.region ?? ""}-${index}`}>
              <div
                className="row"
                style={{ justifyContent: "space-between", alignItems: "start" }}
              >
                <span className="issue__text">{text.title}</span>
                <StatusBadge tone={SEVERITY_TONES[issue.severity]}>
                  {SEVERITY_LABELS[issue.severity]}
                </StatusBadge>
              </div>
              {text.detail && <span className="muted">{text.detail}</span>}
              {issue.region && (
                <span className="faint">{COVERAGE_REGION_LABELS[issue.region]}</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
